import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Activity, ShieldCheck, Stethoscope, MessageSquareHeart } from "lucide-react";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const QUICK = [
  { role: "Super-Admin", email: "super@projexino.com", password: "admin123", icon: ShieldCheck, color: "from-[#004a99] to-[#0066d6]" },
  { role: "Clinic-Admin (Apollo)", email: "doctor@apollo.com", password: "doctor123", icon: Stethoscope, color: "from-[#f37021] to-[#ff8a3d]" },
  { role: "Clinic-Admin (MediCare)", email: "doctor@medicare.com", password: "doctor123", icon: Stethoscope, color: "from-[#0f766e] to-[#10b981]" },
  { role: "Patient", email: "patient@demo.com", password: "patient123", icon: MessageSquareHeart, color: "from-[#7c3aed] to-[#a855f7]" },
];

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (e, em, pw) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const u = await login(em ?? email, pw ?? password);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "super_admin") navigate("/super-admin");
      else if (u.role === "patient") navigate("/patient");
      else navigate("/clinic");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen app-bg grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative bg-[#004a99] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#f37021]/20 blur-3xl" />
        <div className="absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/15 ring-1 ring-white/30 flex items-center justify-center">
            <Activity className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/70 font-semibold">Projexino</div>
            <div className="text-2xl font-heading font-bold">Pulse-Agent AI</div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <h1 className="text-5xl font-heading font-bold leading-[1.05] tracking-tight">
            The healthcare clinic, <span className="text-[#f37021]">on autopilot.</span>
          </h1>
          <p className="mt-5 text-white/80 leading-relaxed text-lg">
            Triage on WhatsApp. Recover overdue revenue automatically.
            Convert voice notes into structured prescriptions.
            All in one white-labeled, multi-tenant platform.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { k: "Revenue Recovered", v: "₹4.3 L+" },
              { k: "Time Saved / Mo", v: "62 hrs" },
              { k: "Triage Accuracy", v: "94%" },
            ].map((s) => (
              <div key={s.k} className="rounded-xl bg-white/10 ring-1 ring-white/15 p-4">
                <div className="text-2xl font-bold">{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-white/70 mt-1">{s.k}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/60">© Projexino · Modern Clinical Glassmorphism</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#004a99] flex items-center justify-center">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Projexino</div>
              <div className="text-xl font-heading font-bold">Pulse-Agent AI</div>
            </div>
          </div>

          <h2 className="text-3xl font-heading font-bold tracking-tight">{t("login_title")}</h2>
          <p className="text-slate-500 mt-1">{t("login_subtitle")}</p>

          <form onSubmit={doLogin} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">{t("login_email")}</Label>
              <Input id="email" type="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@clinic.com"
                     data-testid="login-email-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">{t("login_password")}</Label>
              <Input id="password" type="password" required value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     placeholder="••••••••"
                     data-testid="login-password-input" className="mt-1" />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[#f37021] hover:bg-[#d9621b] text-white font-semibold rounded-lg h-11"
              data-testid="login-submit-button"
            >
              {busy ? "…" : t("login_submit")}
            </Button>
          </form>

          <div className="mt-8">
            <div className="tiny-label mb-3">{t("login_quick")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {QUICK.map((q) => (
                <button
                  key={q.email}
                  onClick={(e) => doLogin(e, q.email, q.password)}
                  className={`group text-left rounded-xl p-3 ring-1 ring-slate-200 hover:ring-[#004a99] transition bg-white tilt-card`}
                  data-testid={`quick-login-${q.email.split("@")[0]}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${q.color} flex items-center justify-center text-white`}>
                      <q.icon className="h-4 w-4" />
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{q.role}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{q.email}</div>
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-xl border-2 border-dashed border-[#f37021] bg-[#fff8f3] p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{t("login_new_clinic")}</div>
                <div className="text-xs text-slate-500">{t("login_new_clinic_sub")}</div>
              </div>
              <button onClick={() => navigate("/signup-clinic")} className="bg-[#f37021] hover:bg-[#d9621b] text-white text-sm font-semibold rounded-lg px-4 py-2 transition" data-testid="open-signup-clinic">
                {t("login_new_clinic_cta")}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="fixed top-4 right-4 z-50"><LanguageSwitcher /></div>
    </div>
  );
}
